import { common } from '@/features/i18n/common';
import '@/ui/style.css';
import { Auth0Context, Auth0Provider } from '@auth0/auth0-react';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { AxiosError } from 'axios';
import * as React from 'react';
import ReactDOM from 'react-dom/client';
import { ErrorComponent } from '@/ui/components/error';
import { NotFound } from '@/ui/components/not-found';
import { AnchoredToastProvider, ToastProvider } from '@/ui/components/toast';
import { TooltipProvider } from '@/ui/components/tooltip';
import { Auth0Exporter, devBypassAuth0, useAuth } from '@/features/auth';
import { devUserId, redirectToSessionEnded } from '@/features/auth/utils';
import { routeTree } from './routeTree.gen';
import { CSPostHogProvider, posthog } from '@/features/posthog/components/posthog-provider';
import { initSentry } from '@/sentry';
import { IntlWrapper } from '@/features/i18n/provider';
import { t } from '@/features/i18n/utils';
import { Spinner } from '@/ui/components/spinner';
import { toastManager } from '@/ui/components/toast';
import { unpackApiErr } from '@/api';
import {
  getAnalysisRunConflictDetail,
  showAnalysisRunConflictToast,
} from '@/features/workspaces/utils';
import type { AnalysisRunConflictResponse } from '@/features/workspaces/types';

// Browser translation extensions wrap React-managed text nodes in <font> tags,
// causing removeChild/insertBefore to fail during reconciliation. (JAVASCRIPT-NEXTJS-51)
const origRemoveChild = Node.prototype.removeChild;
Node.prototype.removeChild = function <T extends Node>(child: T): T {
  if (child.parentNode !== this) return child;
  return origRemoveChild.call(this, child) as T;
};
const origInsertBefore = Node.prototype.insertBefore;
Node.prototype.insertBefore = function <T extends Node>(newNode: T, refNode: Node | null): T {
  if (refNode && refNode.parentNode !== this) return newNode;
  return origInsertBefore.call(this, newNode, refNode) as T;
};

const preloadReloadKey = 'vite-preload-reload';
window.addEventListener('vite:preloadError', () => {
  if (Date.now() - Number(sessionStorage.getItem(preloadReloadKey)) < 10_000) return;
  sessionStorage.setItem(preloadReloadKey, String(Date.now()));
  window.location.reload();
});

const onQueryError = (error: Error) => {
  if ((error as AxiosError).response?.status === 401) {
    redirectToSessionEnded('sessionExpired');
  }
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 300 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    },
  },
  queryCache: new QueryCache({ onError: onQueryError }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      const response = (
        error as AxiosError<
          Partial<AnalysisRunConflictResponse> & {
            detail?: { code?: string; readiness?: { resources: { failed: number }[] } };
          }
        >
      ).response;
      if (response?.status === 401) return redirectToSessionEnded('sessionExpired');
      if (
        response?.status === 409 &&
        response.data?.detail &&
        typeof response.data.detail === 'object'
      ) {
        if (response.data.detail.code === 'WORKSPACE_LIMIT_REACHED')
          return toastManager.add({
            type: 'error',
            title: t({
              en: 'Workspace limit reached. Ask an admin to add more workspaces to your agreement.',
              nb: 'Grensen for arbeidsområder er nådd. Be en administrator om å legge til flere arbeidsområder i avtalen.',
            }),
          });
        const failedCount = response.data.detail.readiness?.resources.reduce(
          (sum, resource) => sum + resource.failed,
          0,
        );
        if (failedCount) {
          return toastManager.add({
            type: 'error',
            title: t({
              en: 'Some files could not be processed',
              nb: 'Noen filer kunne ikke behandles',
            }),
            description: t({
              en:
                failedCount === 1
                  ? `${failedCount} file failed processing. It is marked as Failed in the file list — hover the badge for the reason, and re-upload a fixed file to retry.`
                  : `${failedCount} files failed processing. They are marked as Failed in the file list — hover the badges for the reasons, and re-upload fixed files to retry.`,
              nb:
                failedCount === 1
                  ? `${failedCount} fil feilet under behandling. Den er merket som Feilet i fillisten — hold musepekeren over merket for årsaken, og last opp en rettet fil for å prøve på nytt.`
                  : `${failedCount} filer feilet under behandling. De er merket som Feilet i fillisten — hold musepekeren over merkene for årsakene, og last opp rettede filer for å prøve på nytt.`,
            }),
          });
        }
        const detail = getAnalysisRunConflictDetail(response.data.detail);
        if (typeof detail.status === 'number') {
          return showAnalysisRunConflictToast(detail.status, mutation.meta?.conflictStatusValues);
        }
      }
      if (mutation.options.onError) return;
      toastManager.add({
        type: 'error',
        title: unpackApiErr(error) || t(common.notifications.error),
      });
    },
  }),
});

const AuthProvider = devUserId
  ? ({ children }: { children: React.ReactNode }) => (
      <Auth0Context.Provider value={devBypassAuth0}>{children}</Auth0Context.Provider>
    )
  : ({ children }: { children: React.ReactNode }) => (
      <Auth0Provider
        domain={import.meta.env.VITE_AUTH0_DOMAIN}
        clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
        authorizationParams={{
          redirect_uri: `${window.location.origin}/auth0/callback`,
          audience: import.meta.env.VITE_AUTH0_AUDIENCE,
        }}
        skipRedirectCallback
        cacheLocation="localstorage"
        useRefreshTokens
      >
        {children}
      </Auth0Provider>
    );

const router = createRouter({
  routeTree,
  scrollRestoration: true,
  getScrollRestorationKey: (location) => location.pathname,
  context: { queryClient, auth: undefined! },
  defaultPreload: 'intent',
  defaultPendingMs: 150,
  defaultPendingMinMs: 200,
  defaultPreloadStaleTime: 0,
  defaultNotFoundComponent: NotFound,
  defaultPendingComponent: () => <Spinner kind="inset" />,
  defaultErrorComponent: ({ error }) => <ErrorComponent className="pt-20 md:pt-40" error={error} />,
  Wrap: ({ children }) => (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <CSPostHogProvider>
          <IntlWrapper>
            <Auth0Exporter />
            <ToastProvider>
              <AnchoredToastProvider>
                <TooltipProvider delay={250}>{children}</TooltipProvider>
              </AnchoredToastProvider>
            </ToastProvider>
          </IntlWrapper>
        </CSPostHogProvider>
      </AuthProvider>
    </QueryClientProvider>
  ),
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

initSentry(router);

if (typeof window !== 'undefined' && posthog) {
  router.subscribe('onLoad', ({ toLocation }) => {
    let url = window.origin + toLocation.pathname;
    if (toLocation.searchStr) {
      url = url + toLocation.searchStr;
    }
    posthog.capture('$pageview', { $current_url: url });
  });
}

const rootElement = document.getElementById('app')!;
if (!rootElement.innerHTML || rootElement.innerHTML.trim().length === 0) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <InnerApp />
    </QueryClientProvider>
  );
}

function InnerApp() {
  const auth = useAuth();
  return <RouterProvider router={router} context={{ auth, queryClient }} />;
}
